import { last, isFinite } from 'lodash-es'

import deline from './deline'
import envRaw from './env-raw'
import Emitter from './emitter'
import StrictObj from './strict-obj'
import debugFields from './debug-fields'

import { onEventPassive } from './on-event'

export default embedResizeProvider()

function embedResizeProvider() {
  const positionRE = /GalleryPosition/
  const frameWidthRE = /:m=(\d+\.?\d*)/    // gallery iframe width
  const frameHeightRE = /:g=(\d+\.?\d*)/   // gallery iframe height
  const vportOffsetRE = /:b=(-?\d+\.?\d*)/ // offset from top of viewport to bottom of gallery
  const vportHeightRE = /:w=(\d+\.?\d*)/   // browser viewport height

  const containerRE = /GalleryContainerSize/
  const containerWidthRE = /:cw=(\d+\.?\d*)/
  const containerHeightRE = /:ch=(\d+\.?\d*)/

  let lastFrameWidth = null
  let lastFrameHeight = null
  let lastVportOffset = null
  let lastVportHeight = null

  let lastContainerWidth = null
  let lastContainerHeight = null

  // subscribe on event
  onEventPassive(window, 'message', messageHandler)

  // define embedResize
  const embedResize = StrictObj(debugFields({
    position: Emitter('embedResize::position'),
    frameWidth: Emitter('embedResize::frameWidth'),
    frameHeight: Emitter('embedResize::frameHeight'),
    viewportOffset: Emitter('embedResize::viewportOffset'),
    viewportHeight: Emitter('embedResize::viewportHeight'),

    container: Emitter('embedResize::container'),
    containerWidth: Emitter('embedResize::containerWidth'),
    containerHeight: Emitter('embedResize::containerHeight'),

    isPositionReady,
    getFrameWidth,
    getFrameHeight,
    getViewportOffset,
    getViewportHeight,

    isContainerReady,
    getContainerWidth,
    getContainerHeight
  }, {
    _lastSize: lastSize,
    _messageHandler: messageHandler,
    _positionHandler: positionHandler,
    _containerHandler: containerHandler
  }))

  // export
  return embedResize

  // position
  function isPositionReady() {
    return true
      && isFinite(lastFrameWidth)
      && isFinite(lastFrameHeight)
      && isFinite(lastVportOffset)
      && isFinite(lastVportHeight)
  }

  function getFrameWidth() {
    return lastFrameWidth
  }

  function getFrameHeight() {
    return lastFrameHeight
  }

  function getViewportOffset() {
    return lastVportOffset
  }

  function getViewportHeight() {
    return lastVportHeight
  }

  // container
  function isContainerReady() {
    return true
      && isFinite(lastContainerWidth)
      && isFinite(lastContainerHeight)
  }

  function getContainerWidth() {
    return lastContainerWidth
  }

  function getContainerHeight() {
    return lastContainerHeight
  }

  // lastSize
  function lastSize() {
    return {
      lastFrameWidth,
      lastFrameHeight,
      lastVportOffset,
      lastVportHeight,
      lastContainerWidth,
      lastContainerHeight
    }
  }

  // messageHandler
  function messageHandler({ data }) {
    if (positionRE.test(data)) {
      return positionHandler(data)
    }
    if (containerRE.test(data)) {
      return containerHandler(data)
    }
  }

  function positionHandler(position) {
    const frameWidth = parseInt(last(frameWidthRE.exec(position)))
    const frameHeight = parseInt(last(frameHeightRE.exec(position)))
    const vportOffset = parseInt(last(vportOffsetRE.exec(position)))
    const vportHeight = parseInt(last(vportHeightRE.exec(position)))

    const frameWidthChanged = frameWidth !== lastFrameWidth
    const frameHeightChanged = frameHeight !== lastFrameHeight
    const vportOffsetChanged = vportOffset !== lastVportOffset
    const vportHeightChanged = vportHeight !== lastVportHeight

    const someChanged = false
      || frameWidthChanged
      || frameHeightChanged
      || vportOffsetChanged
      || vportHeightChanged

    if (frameWidthChanged) {
      lastFrameWidth = frameWidth
      embedResize.frameWidth.emit(lastFrameWidth)
    }

    if (frameHeightChanged) {
      lastFrameHeight = frameHeight
      embedResize.frameHeight.emit(lastFrameHeight)
    }

    if (vportOffsetChanged) {
      lastVportOffset = vportOffset
      embedResize.viewportOffset.emit(lastVportOffset)
    }

    if (vportHeightChanged) {
      lastVportHeight = vportHeight
      embedResize.viewportHeight.emit(lastVportHeight)
    }

    if (someChanged) {
      embedResize.position.emit(
        lastFrameWidth,
        lastFrameHeight,
        lastVportOffset,
        lastVportHeight)
    }

    if (envRaw.showsEmbedResizeDebug) {
      console.debug(deline(`
        ${Date.now()} !embedResize#position
          [${ frameWidthChanged ? 'FW' : '--' }]
          [${ frameHeightChanged ? 'FH' : '--' }]
          [${ vportOffsetChanged ? 'VO' : '--' }]
          [${ vportHeightChanged ? 'VH' : '--' }]:
          { frameWidth: ${frameWidth}, frameHeight: ${frameHeight}, vportOffset: ${vportOffset}, vportHeight: ${vportHeight} }`))
    }
  }

  function containerHandler(container) {
    const containerWidth = parseInt(last(containerWidthRE.exec(container)))
    const containerHeight = parseInt(last(containerHeightRE.exec(container)))

    const containerWidthChanged = containerWidth !== lastContainerWidth
    const containerHeightChanged = containerHeight !== lastContainerHeight

    const someChanged = false
      || containerWidthChanged
      || containerHeightChanged

    if (containerWidthChanged) {
      lastContainerWidth = containerWidth
      embedResize.containerWidth.emit(lastContainerWidth)
    }

    if (containerHeightChanged) {
      lastContainerHeight = containerHeight
      embedResize.containerHeight.emit(lastContainerHeight)
    }

    if (someChanged) {
      embedResize.container.emit(
        lastContainerWidth,
        lastContainerHeight)
    }

    if (envRaw.showsEmbedResizeDebug) {
      console.debug(deline(`
        ${Date.now()} !embedResize#container
          [${ containerWidthChanged ? 'CW' : '--' }]
          [${ containerHeightChanged ? 'CH' : '--' }]
          { containerWidth: ${containerWidth}, containerHeight: ${containerHeight} }`))
    }
  }
}
